import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import postal from 'postal';
import { selectInput } from '../util/inputUtils';

function TextBox({
  value,
  placeholder = undefined,
  onChange = undefined,
  onPaste = undefined,
  disabled = false,
  // dark,
  error = false,
  red = false,
  alignRight = false,
  alignCenter = false,
  onEnter = undefined,
  password = false,
  onTabNext = undefined,
  onTabPrevious = undefined,
  maxLength = undefined,
  textId = undefined,
  noborder = false,
  noLeftBorder = false,
  underline = false,
  onFocus,
  backgroundColor,
  onBlur,
  focusableWhenDisabled = false,
  ...other
}:{
  value:string|null|undefined,
  placeholder?:string|null|undefined,
  onChange?: ((e:React.ChangeEvent<HTMLInputElement>) => void)|undefined,
  onPaste?: ((e:React.ClipboardEvent<HTMLInputElement>) =>void)|undefined,
  disabled?: boolean,
  red?: boolean,
  error?: boolean,
  alignRight?: boolean,
  alignCenter?: boolean,
  onEnter?: (() => void)|undefined,
  password?: boolean,
  onTabNext?: (() => boolean)|undefined,
  onTabPrevious?: (() => boolean)|undefined,
  maxLength?: number|undefined,
  textId?: string|undefined,
  noborder?: boolean,
  noLeftBorder?: boolean,
  underline?: boolean,
  onFocus?: (inp: HTMLInputElement) => void,
  backgroundColor?: string,
  onBlur?: ((x?: any) => Promise<boolean | undefined> | void) | undefined,
  [x:string]: any
}) {
  const autocompleteOffGuard = _.uniqueId('autocomplete-off-');
  const dark = false;
  const channel = postal.channel('ifms');
  const inputId = textId || _.uniqueId('ifms-text-');

  function initializeEvent() {
    document.getElementById(inputId)?.addEventListener('keydown', onKeyDown);
  }

  function unInitializeEvent() {
    document.getElementById(inputId)?.removeEventListener('keydown', onKeyDown);
  }

  useEffect(() => {
    if (inputId) {
      initializeEvent();
      return unInitializeEvent;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputId]);

  
  let cssClass = dark ? 'e-ifms-input-dark e-outline ifms-tabable' : 'e-ifms-input e-outline ifms-tabable';
  if(disabled && focusableWhenDisabled){
    cssClass = `${cssClass} e-ifms-focusable-when-disabled`;
  }
  if (noborder) {
    cssClass = `${cssClass} ifms-noborder-input`;
  }
  if (noLeftBorder) {
    cssClass = `${cssClass} ifms-noleftborder-input`;
  }

  const initialStyle = !!other.style ? other.style : {};
  let textStyle = red ? { ...initialStyle, color: '#bf0000' } : initialStyle;
  if (alignRight) {
    textStyle = {...textStyle, textAlign: 'right'};
  }
  if (alignCenter) {
    textStyle = {...textStyle, textAlign: 'center'};
  }
  if (underline) {
    textStyle = {...textStyle, textDecoration: 'underline'};
  }
  if (error) {
    cssClass = `${cssClass} e-ifms-input-error`;
  }
  if(backgroundColor) {
    textStyle = {...textStyle, backgroundColor};
  }
  if (!disabled) {
    cssClass = `${cssClass} ifms-tabable-active`;
  }

  function changeWrapper(e:React.ChangeEvent<HTMLInputElement>) {
    if (maxLength !== undefined && maxLength !== null && maxLength > 0) {
      if (e.target.value && e.target.value.length > maxLength) {
        return;
      }
    }

    if (onChange) {
      onChange(e);
    }
  }

  function processTabNext(e:KeyboardEvent) {
    if (onTabNext) {
      const tabProcessed = onTabNext();
      if (tabProcessed) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }
  }

  function processTabPrevious(e:KeyboardEvent) {
    if (onTabPrevious) {
      const tabProcessed = onTabPrevious();
      if (tabProcessed) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }
  }

  function isCell(element: HTMLInputElement): boolean {
    const divElements = document.querySelectorAll('div[id$="-editabletable"]');
    return Array.from(divElements).some(div => div.contains(element));
  }
  
  async function handleBlur(e: React.FocusEvent) {
    const target = e.target as HTMLInputElement;
    const relatedTarget = e.relatedTarget as HTMLElement;
    const nativeEvent = e.nativeEvent;

    if (relatedTarget?.dataset?.nocellvalidate === 'true') {
      return;
    }
    if (relatedTarget?.classList.contains("ReactModal__Content")) {
      return;
    }

    if (onBlur) {
      const result = await onBlur(e);
      setTimeout(() => {
        if (result === true) {
          if (isCell(target)) {
            channel.publish('ifms.editable-table-onEnter', { backFocus: false, changeFocus: true, element: target });
          }
        }
        else if (result === false)
          selectInput(target)
      }, 10);
    }
  }

  async function onKeyDown(e: KeyboardEvent) {
    const target = e.target as HTMLInputElement;

    async function handleBlurValidation(changeFocus: boolean) {
      if (!onBlur || !isCell(target)) return false;

      e.stopPropagation();
      e.preventDefault();
      const result = await onBlur(e);

      if (result) {
        channel.publish('ifms.editable-table-onEnter', { backFocus: e.shiftKey, changeFocus, element:target });
        return true;
      }

      setTimeout(() => selectInput(target), 10);
      return false;
    }

    if (focusableWhenDisabled && disabled) {
      if (!(e.ctrlKey && (e.keyCode === 67 || e.keyCode === 65))) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }

    if (e.key === 'Enter') {
      onEnter?.();
      await handleBlurValidation(false);
      return;
    }

    if (e.key === 'Tab') {
      const validated = await handleBlurValidation(true);
      if (!validated) {
        if (e.shiftKey) {
          processTabPrevious(e);
        } else {
          processTabNext(e);
        }
      }
    }
  }

  function handleFocus(e:React.FocusEvent<HTMLInputElement>) {
    if (onFocus) {
      onFocus(e.target);
    }
  }

  const type = password ? 'password' : 'text';

  return (
    <input
      {..._.omit(other, ['dark'])}
      id={inputId}
      type={type}
      style={textStyle}
      className={cssClass}
      value={value || ''}
      onChange={changeWrapper}
      disabled={disabled && !focusableWhenDisabled}
      placeholder={placeholder ?? undefined}
      //onKeyDown={onKeyDown}
      onBlur={handleBlur}
      //autoComplete={autocompleteOffGuard}
      autoComplete="off"
      list={autocompleteOffGuard}
      onPaste={onPaste}
      data-tooltip="-from-value"
      
      onFocus={handleFocus}
      data-tooltip-onlywhencutoff
    />
  );
}

TextBox.defaultProps = {
  onChange: () => {},
  onPaste: () => {},
  disabled: false,
  dark: false,
  placeholder: '',
  red: false,
  error: false,
  alignRight: false,
  alignCenter: false,
  password: false,
  noborder: false,
  noLeftBorder: false,
  underline: false,  
  focusableWhenDisabled: false
};

TextBox.propTypes = {
  onChange: PropTypes.func.isRequired,
  onPaste: PropTypes.func.isRequired,
  onEnter: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  noborder: PropTypes.bool.isRequired,
  noLeftBorder: PropTypes.bool.isRequired,
  dark: PropTypes.bool.isRequired,
  red: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  alignRight: PropTypes.bool.isRequired,
  alignCenter: PropTypes.bool.isRequired,
  password: PropTypes.bool.isRequired,
  underline: PropTypes.bool.isRequired,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  maxLength: PropTypes.number,
  textId: PropTypes.string,
  onFocus: PropTypes.func,
  backgroundColor: PropTypes.string,
  focusableWhenDisabled: PropTypes.bool,
};

export default React.memo(TextBox);
